import React, { FC, useEffect, useState } from "react";
import {
  Form, Input, Switch, message, Spin, Table, Tag, Empty,
} from "antd";
import { PlusOutlined, ArrowLeftOutlined, EditOutlined, TeamOutlined } from "@ant-design/icons";
import { BASE_URL } from "../../../constants/urlConstants";
import "./AddEmp.css";

const { TextArea } = Input;

const AddEmp: FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading]               = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [data, setData]                     = useState<any[]>([]);
  const [showForm, setShowForm]             = useState(false);
  const [editingId, setEditingId]           = useState<number | null>(null);

  /* ── FETCH ── */
  const fetchEmployees = async () => {
    try {
      setInitialLoading(true);
      const res = await fetch(`${BASE_URL}/api/finalle/employees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roleType: 4 }),
      });
      if (!res.ok) throw new Error("Failed to fetch employees");
      setData(await res.json());
    } catch (err: any) { message.error(err.message); }
    finally { setInitialLoading(false); }
  };

  useEffect(() => { fetchEmployees(); }, []);

  /* ── SUBMIT ── */
  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        roleType: 4,
        mobile: values.mobile,
        username: values.username,
        password: values.password,
        email: values.email,
        address: values.address,
        area: values.area,
        status: values.status ?? true,
      };
      const isEdit = !!editingId;
      const res = await fetch(
        isEdit
          ? `${BASE_URL}/api/finalle/employee/update/${editingId}`
          : `${BASE_URL}/api/finalle/employee`,
        {
          method: isEdit ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error(isEdit ? "Update failed" : "Creation failed");
      message.success(isEdit ? "Employee updated!" : "Employee added!");
      form.resetFields(); setShowForm(false); setEditingId(null); fetchEmployees();
    } catch (err: any) { message.error(err.message || "Something went wrong"); }
    finally { setLoading(false); }
  };

  /* ── EDIT ── */
  const handleEdit = (record: any) => {
    setEditingId(record.id); setShowForm(true);
    form.setFieldsValue({
      username: record.username,
      mobile: record.mobile,
      email: record.email,
      password: "",
      address: record.address,
      area: record.area,
      status: record.status,
    });
  };

  const handleCancel = () => { setShowForm(false); setEditingId(null); form.resetFields(); };

  /* ── TABLE COLUMNS ── */
  const columns = [
    { title: "Name",   dataIndex: "username" },
    { title: "Mobile", dataIndex: "mobile", width: 120 },
    { title: "Area",   dataIndex: "area"    },
    {
      title: "Status", width: 100,
      render: (r: any) => r.status
        ? <Tag color="success" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11 }}>Active</Tag>
        : <Tag color="error"   style={{ borderRadius: 2, fontWeight: 600, fontSize: 11 }}>Inactive</Tag>,
    },
    {
      title: "Action", width: 80,
      render: (r: any) => (
        <button className="emp-edit-btn" onClick={() => handleEdit(r)}>
          <EditOutlined /> Edit
        </button>
      ),
    },
  ];

  if (initialLoading) return (
    <div className="emp-loader"><Spin size="large" /><p>Loading employees…</p></div>
  );

  return (
    <div className="emp-page">

      {/* ── Top bar ── */}
      <div className="emp-topbar">
        <div className="emp-topbar-left">
          <TeamOutlined className="emp-topbar-icon" />
          <span className="emp-topbar-title">Employees</span>
          {data.length > 0 && <span className="emp-topbar-badge">{data.length} total</span>}
        </div>
        {!showForm && (
          <button className="emp-add-btn" onClick={() => setShowForm(true)}>
            <PlusOutlined /> Add Employee
          </button>
        )}
      </div>

      {/* ── Breadcrumb ── */}
      <div className="emp-breadcrumb">
        Dashboard &rsaquo; Employees{showForm && <> &rsaquo; {editingId ? "Edit" : "Add New"}</>}
      </div>

      {/* ══ FORM VIEW ══ */}
      {showForm ? (
        <div className="emp-form-body">
          <button className="emp-back-btn" onClick={handleCancel}>
            <ArrowLeftOutlined /> Back
          </button>

          <div className="emp-form-card">
            <div className="emp-form-card-header">
              <TeamOutlined />
              <span>{editingId ? "Edit Employee" : "New Employee"}</span>
              {editingId && <span className="emp-form-id-badge">#{editingId}</span>}
            </div>

            <div className="emp-form-card-body">
              <Form form={form} layout="vertical" onFinish={onFinish}
                initialValues={{ status: true }}>

                <div className="emp-section-label">Account Details</div>

                <div className="emp-form-row">
                  <Form.Item name="username" label="Username" rules={[{ required: true, message: "Required" }]}>
                    <Input size="large" placeholder="Full name" />
                  </Form.Item>
                  <Form.Item
                    name="password" label="Password"
                    rules={[{ required: !editingId, message: "Password required" }]}
                  >
                    <Input.Password size="large"
                      placeholder={editingId ? "Leave blank to keep same" : "Set password"} />
                  </Form.Item>
                </div>

                <div className="emp-form-row">
                  <Form.Item name="mobile" label="Mobile"
                    rules={[{ required: true }, { pattern: /^\d{10}$/, message: "10 digits" }]}>
                    <Input size="large" maxLength={10} placeholder="10-digit number" />
                  </Form.Item>
                  <Form.Item name="email" label="Email"
                    rules={[{ required: true }, { type: "email", message: "Valid email" }]}>
                    <Input size="large" placeholder="email@example.com" />
                  </Form.Item>
                </div>

                <div className="emp-section-label">Location</div>

                <Form.Item name="address" label="Address" rules={[{ required: true, message: "Required" }]}>
                  <TextArea rows={3} placeholder="Full address" />
                </Form.Item>

                <Form.Item name="area" label="Area" rules={[{ required: true, message: "Required" }]}>
                  <Input size="large" placeholder="e.g. Kurnool South" />
                </Form.Item>

                <div className="emp-section-label">Status</div>

                <div className="emp-switch-item">
                  <Form.Item name="status" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                  <div>
                    <p className="emp-switch-label">Active Status</p>
                    <p className="emp-switch-hint">Enable or disable this employee</p>
                  </div>
                </div>

                <div className="emp-form-actions">
                  <button type="submit" className={`emp-submit-btn${loading ? " loading" : ""}`} disabled={loading}>
                    {loading ? "Saving…" : editingId ? "Update Employee" : "Add Employee"}
                  </button>
                  <button type="button" className="emp-cancel-btn" onClick={handleCancel}>Cancel</button>
                </div>
              </Form>
            </div>
          </div>
        </div>

      ) : data.length === 0 ? (
        <div className="emp-empty">
          <Empty description={<span className="emp-empty-text">No employees found.</span>} />
          <button className="emp-add-btn" style={{ marginTop: 20 }} onClick={() => setShowForm(true)}>
            <PlusOutlined /> Add Employee
          </button>
        </div>

      ) : (
        <div className="emp-table-wrap">

          {/* Desktop table */}
          <div className="emp-table-desktop">
            <Table dataSource={data} columns={columns} rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              className="emp-table" rowClassName={() => "emp-table-row"}
              scroll={{ x: true }} />
          </div>

          {/* Mobile card list */}
          <div className="emp-table-mobile">
            <div className="emp-card-list">
              {data.map((r: any) => (
                <div key={r.id} className="emp-dist-card">
                  <div className="emp-dist-card-header">
                    <span className="emp-dist-card-name">{r.username}</span>
                    {r.status
                      ? <Tag color="success" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11, margin: 0 }}>Active</Tag>
                      : <Tag color="error"   style={{ borderRadius: 2, fontWeight: 600, fontSize: 11, margin: 0 }}>Inactive</Tag>
                    }
                  </div>
                  <div className="emp-dist-card-body">
                    <div className="emp-dist-card-row">
                      <span className="emp-dist-card-row-label">Mobile</span>
                      <span>{r.mobile}</span>
                    </div>
                    <div className="emp-dist-card-row">
                      <span className="emp-dist-card-row-label">Area</span>
                      <span>{r.area}</span>
                    </div>
                    <div className="emp-dist-card-row">
                      <span className="emp-dist-card-row-label">Email</span>
                      <span style={{ wordBreak: "break-all" }}>{r.email}</span>
                    </div>
                  </div>
                  <div className="emp-dist-card-footer">
                    <button className="emp-edit-btn" onClick={() => handleEdit(r)}>
                      <EditOutlined /> Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default AddEmp;